import { lineString, nearestPointOnLine, point } from "@turf/turf";
import React, { useEffect, useRef, useState } from "react";
import { uniqBy } from "lodash";
import {
  GoogleMap,
  OverlayView,
  Polyline,
  withGoogleMap,
  Marker,
} from "react-google-maps";
import { compose, withProps } from "recompose";
import * as turf from "@turf/turf";
import { decode } from "@mapbox/polyline";

import Bus from "../../assets/school-bus.svg";
import "./styles.scss";

const RNMapComp = compose(
  withProps({
    loadingElement: <div id="gmap-native-loading" style={{ height: `100%` }} />,
    containerElement: (
      <div
        id="gmap-container"
        style={{ height: `50%`, position: "relative" }}
      />
    ),
    mapElement: (
      <div
        id="gmap-map"
        style={{
          height: `100%`,
          width: "100%",
          position: "absolute",
          zIndex: 1,
        }}
      />
    ),
  }),
  withGoogleMap
)((props) => {
  const map = useRef(null);
  const { path, bounds, stops, court, vehicleLocations, traffic, RNRoute } =
    props;
  const [canUpdateBound, setCanUpdateBound] = useState(true);
  const [cars, setCars] = useState([]);

  const [isLoaded, setIsLoaded] = useState(false);
  const [carPaths, setCarPaths] = useState([]);
  const [trigger, setTrigger] = useState(false);

  useEffect(() => {
    if (map.current && canUpdateBound) {
      map.current.fitBounds(bounds, 10);
      setCanUpdateBound(false);
    }
  }, [bounds, canUpdateBound, map]);

  useEffect(() => {
    if (vehicleLocations && path.length > 0) {
      const tempCars = uniqBy(vehicleLocations, "license_plate")
        .map((el) => {
          // console.log(("el", el));
          const pathLine = turf.lineString(
            path
              .find((ele) => ele.routeId === el.routeId)
              .shape.map((el) => [el.lng, el.lat])
          );
          const currentLog = turf.point(el.coordinates);
          const logToLineDistance = turf.pointToLineDistance(
            currentLog,
            pathLine,
            {
              units: "meters",
            }
          );

          // check vehicle location is 100m away from polyline
          if (logToLineDistance < 100 && pathLine) {
            const pathLength = turf.length(pathLine, { units: "meters" });

            if (el.progress) {
              // console.log(el.progress, pathLength, pathLine);
              // check if progress exists, then bind to ±25% (or param) of line
              const start = el.progress - pathLength * 0.15;
              const end = el.progress + pathLength * 0.15;
              // console.log("start, end", start, end);
              const slicedLine = turf.lineSliceAlong(
                pathLine,
                start >= 0 ? start : 0,
                end <= pathLength ? end : pathLength,
                { units: "meters" }
              );

              const tempCoor = turf.nearestPointOnLine(
                slicedLine,
                point(el.coordinates)
              );

              return {
                ...el,
                coordinates: tempCoor.geometry.coordinates,
                path: slicedLine.geometry.coordinates,
              };
            } else {
              const tempCoor = turf.nearestPointOnLine(
                pathLine,
                point(el.coordinates)
              );
              return {
                ...el,
                coordinates: tempCoor.geometry.coordinates,
                path: pathLine.geometry.coordinates,
              };
            }
          }
        })
        .filter((el) => el);
      // console.log(tempCars);
      setCars(tempCars);
    }
  }, [path, vehicleLocations]);

  useEffect(() => {
    if (court !== "rmb" && cars.length > 0) {
      const tempCarPaths = cars.map((el) => {
        const carPath = carPaths.find(
          (ele) => ele.license_plate === el.license_plate
        );

        if (carPath?.lastLog.length > 0 && el?.coordinates.length > 0) {
          const pathLine = turf.lineString(el.path);
          const currentLoc = turf.point(el.coordinates);
          const lastLoc = turf.point(carPath.lastLog);

          const recalSlice = turf
            .lineSlice(lastLoc, currentLoc, pathLine)
            .geometry.coordinates.map((el) => ({ lat: el[1], lng: el[0] }));

          // console.log(
          //   el.license_plate,
          //   el?.coordinates,
          //   carPath,
          //   el?.coordinates?.[1].toFixed(7) ===
          //     carPath?.path?.[1]?.lat.toFixed(7)
          // );

          if (
            el?.coordinates?.[1].toFixed(7) ===
            carPath?.path?.[1]?.lat.toFixed(7)
          ) {
            // "same loc, no move"
            // console.log("slnm");
            return {
              license_plate: el.license_plate,
              value: carPath.value,
              lastLog: carPath.lastLog,
              path: recalSlice,
            };
          }
          //"different loc, move"
          // console.log("dl,m");
          return {
            license_plate: el.license_plate,
            value: 0,
            lastLog: el.coordinates,
            path: recalSlice,
          };
        }
        // "no last log or current log"
        return {
          license_plate: el.license_plate,
          value: 0,
          lastLog: el.coordinates,
          path: [
            { lat: el.coordinates[1], lng: el.coordinates[0] },
            { lat: el.coordinates[1], lng: el.coordinates[0] },
          ],
        };
      });
      // console.log("tempCarPaths", tempCarPaths);
      setCarPaths(tempCarPaths);
    }
  }, [vehicleLocations, cars]);

  useEffect(() => {
    if (court !== "rmb") {
      const timer = setTimeout(() => {
        if (carPaths.length > 0) {
          setCarPaths(
            carPaths.map((el) => {
              if (el?.value < 100) {
                if (el.value + 5 > 90) {
                  // console.log(el.license_plate, " to 100");
                  setTrigger(false);
                }
                return { ...el, value: el.value + 5 };
              }
              return { ...el, value: el?.value || 0 };
            })
          );
        }
      }, 30);

      return () => clearTimeout(timer);
    }
  }, [carPaths, court, trigger]);

  useEffect(() => {
    if (!isLoaded) {
      setIsLoaded(true);
    }
  }, []);

  const mapStationToRoute = (lat, lng) => {
    const line = lineString(
      path.flatMap((el) =>
        el.shape.map((ele) => {
          return [ele.lng, ele.lat];
        })
      )
    );

    const points = point([lng, lat]);
    const snapped = nearestPointOnLine(line, points);
    const [newLng, newLat] = snapped?.geometry?.coordinates;
    return { lat: newLat, lng: newLng };
  };

  return (
    <div style={{ height: "100%", zIndex: "90" }}>
      {/* Map */}
      <GoogleMap
        defaultCenter={{ lat: 22.27544, lng: 114.15253 }}
        onUnM
        ref={map}
        options={{
          minZoom: 10,
          maxZoom: 18,
          streetViewControl: false,
          scaleControl: false,
          mapTypeControl: false,
          panControl: false,
          zoomControl: false,
          rotateControl: false,
          fullscreenControl: false,
        }}
      >
        {path.map((el) => {
          return (
            <>
              {/* Route Path Border */}
              <Polyline
                path={el.shape}
                options={{
                  strokeColor: `${
                    el.routeId === RNRoute.normalRouteId ? "#3399FF" : "#D96466"
                  }`,
                  strokeWeight: 8,
                  zIndex: el.routeId === RNRoute.normalRouteId ? "99" : "89",
                }}
              />
              {/* Route Path Inner */}
              <Polyline
                path={el.shape}
                options={{
                  strokeColor: `${
                    el.routeId === RNRoute.normalRouteId ? "#59FF5E" : "#ffc9c5"
                  }`,
                  strokeWeight: 3,
                  zIndex: el.routeId === RNRoute.normalRouteId ? "100" : "90",
                }}
              />
            </>
          );
        })}

        {/* Traffic Polyline */}
        {traffic.length > 0 &&
          traffic.map((traffic) => {
            return traffic.traffic.map((el) => {
              const path = decode(el.segment).map((ele) => ({
                lat: ele[0],
                lng: ele[1],
              }));

              return (
                <Polyline
                  path={path}
                  options={{
                    strokeColor: `${
                      el.trafficLevel === "good"
                        ? traffic.routeId === RNRoute.normalRouteId
                          ? "#59FF5E"
                          : "#ffc9c5"
                        : el.trafficLevel === "average"
                        ? "#ffac1c"
                        : el.trafficLevel === "bad"
                        ? "#ff1c56"
                        : "#ab3233"
                    }`,
                    strokeWeight: 3,
                    zIndex: "110",
                  }}
                />
              );
            });
          })}

        {/* Stops Location */}
        {stops && isLoaded
          ? stops.map((station) => {
              return (
                <Marker
                  position={mapStationToRoute(
                    station?.stop.lat,
                    station?.stop.lng
                  )}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 6,
                    fillOpacity: 1,
                    fillColor: "#FFF",
                    strokeWeight: 4,
                    strokeColor: `${court === "rmb" ? "#cd5c5c" : "#3399ff"}`,
                  }}
                />
              );
              // <OverlayView
              //   key={Math.random()}
              //   position={mapStationToRoute(
              //     station?.coordinates.wgs84.latitude,
              //     station?.coordinates.wgs84.longitude
              //   )}
              //   mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              // >
              //   <div className={`station-icon ${court}`}></div>
              // </OverlayView>
            })
          : null}

        {/* Vehicle Icon */}
        {court !== "rmb" &&
          cars &&
          cars.length > 0 &&
          cars.map((car, index) => {
            const offsets = carPaths.find(
              (el) => el.license_plate === car.license_plate
            );
            return offsets?.path.length > 0 ? (
              <Polyline
                path={offsets?.path}
                options={{
                  strokeWeight: 0,
                  // strokeColor: "#f00",
                  strokeOpacity: 1,
                  zIndex: "500",
                  icons: [
                    {
                      icon: {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 14,
                        fillOpacity: 1,
                        zIndex: "501",
                        fillColor: "#3499FE",
                        strokeColor: "#1A73E8",
                        strokeWeight: 2,
                      },
                      offset: `${offsets?.value}%`,
                    },
                    {
                      icon: {
                        path: "m -4.86 -8.66 l 9.72 0 c 1.62 0 2.43 0.81 2.43 2.43 l 0 8.1 l 0 2.43 c 0 1.62 -1.62 1.62 -2.43 1.62 l 0 0.81 c 0 0.81 0 0.81 -0.81 0.81 l -0.81 0 c -0.81 0 -0.81 0 -0.81 -1.62 l -4.86 0 c 0 1.62 0 1.62 -0.81 1.62 l -0.81 0 c -0.81 0 -0.81 0 -0.81 -0.81 l 0 -0.81 c -0.81 0 -2.43 0 -2.43 -1.62 l 0 -0.81 l 0 -1.62 l 0 -8.1 c 0 -1.62 0.81 -2.43 2.43 -2.43 m -0.81 2.43 l 0 4.86 c 4.05 0.81 7.29 0.81 11.34 0 c 0 -1.62 0 -3.24 0 -4.86 c -0.81 -0.81 -10.53 -0.81 -11.34 0 M -6 3 C -6 5 -2 5 -2 2 C -3 2 -6 1 -6 3 M 2 2 C 2 5 6 5 6 3 C 6 1 3 2 2 2",
                        scale: 1,
                        fillOpacity: 1,
                        fillColor: "#FFF",
                        strokeWeight: 0,
                        zIndex: "502",
                      },
                      offset: `${offsets?.value}%`,
                      fixedRotation: true,
                    },
                  ],
                }}
              ></Polyline>
            ) : (
              <Polyline
                path={[
                  { lat: car.coordinates[1], lng: car.coordinates[0] },
                  { lat: car.coordinates[1], lng: car.coordinates[0] },
                ]}
                options={{
                  strokeWeight: 0,
                  strokeOpacity: 1,
                  zIndex: "500",
                  icons: [
                    {
                      icon: {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 14,
                        fillOpacity: 1,
                        zIndex: "501",
                        fillColor: "#3499FE",
                        strokeColor: "#1A73E8",
                        strokeWeight: 2,
                      },
                      offset: `${offsets?.value}%`,
                    },
                    {
                      icon: {
                        path: "m -4.86 -8.66 l 9.72 0 c 1.62 0 2.43 0.81 2.43 2.43 l 0 8.1 l 0 2.43 c 0 1.62 -1.62 1.62 -2.43 1.62 l 0 0.81 c 0 0.81 0 0.81 -0.81 0.81 l -0.81 0 c -0.81 0 -0.81 0 -0.81 -1.62 l -4.86 0 c 0 1.62 0 1.62 -0.81 1.62 l -0.81 0 c -0.81 0 -0.81 0 -0.81 -0.81 l 0 -0.81 c -0.81 0 -2.43 0 -2.43 -1.62 l 0 -0.81 l 0 -1.62 l 0 -8.1 c 0 -1.62 0.81 -2.43 2.43 -2.43 m -0.81 2.43 l 0 4.86 c 4.05 0.81 7.29 0.81 11.34 0 c 0 -1.62 0 -3.24 0 -4.86 c -0.81 -0.81 -10.53 -0.81 -11.34 0 M -6 3 C -6 5 -2 5 -2 2 C -3 2 -6 1 -6 3 M 2 2 C 2 5 6 5 6 3 C 6 1 3 2 2 2",
                        scale: 1,
                        fillOpacity: 1,
                        fillColor: "#FFF",
                        strokeWeight: 0,
                        zIndex: "502",
                      },
                      offset: `${offsets?.value}%`,
                      fixedRotation: true,
                    },
                  ],
                }}
              ></Polyline>
            );
          })}
        {court === "rmb" && cars && cars?.length > 0
          ? cars.map((car) => {
              if (car?.coordinates) {
                return (
                  <OverlayView
                    position={{
                      lat: car?.coordinates?.[1],
                      lng: car?.coordinates?.[0],
                    }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    getPixelPositionOffset={() => ({ x: -15, y: -15 })}
                  >
                    <img src={Bus} style={{ height: 30, width: 30 }} />
                  </OverlayView>
                );
              }
              return <></>;
            })
          : null}
      </GoogleMap>
    </div>
  );
});

export default RNMapComp;
